// Typy pro building log data
export interface ValueDiffItem {
  type: 'value';
  label: string;
  current: string;
  previous: string;
}

export interface ListCollectionDiffItem {
  type: 'list' | 'collection';
  label?: string;
  added: string[] | Record<string, unknown>[];
  removed: string[] | Record<string, unknown>[];
}

// Nový typ pro building log akce
export interface BuildingLogActionItem {
  type: 'action';
  actionType: 'addUnit' | 'removeUnit' | 'addProfile' | 'removeProfile';
  unit?: string;
  unitId?: string;
  profileId?: string;
  profileName?: string;
}

// Provedená operace v rámci řízení (z bezplatného API KN)
export interface PlombaOperace {
  nazev?: string;
  datum?: string;
}

// Insolvenční řízení z ISIR — crawler ukládá řízení jako pole objektů {name, slug, value},
// diff je pak collection, kde added/removed obsahují CELÁ řízení (pole polí). Bez vlastního
// typu to padalo do formatObject a zobrazovalo "0: [object Object], ...".
export interface InsolvencyDiffItem {
  type: 'insolvency';
  change: 'new' | 'updated' | 'removed';
  id?: string;               // interní id řízení v ISIR (odkaz na veřejný detail)
  stav?: string;             // Aktuální stav řízení
  stavPrevious?: string;     // původní stav (jen u change = 'updated' a reálné změny)
  lhuta?: string;            // Konec lhůty pro přihlášení pohledávek
  skonceni?: string;         // Datum skončení insolvenčního řízení
}

// Plomba = probíhající/ukončené řízení v katastru na jednotce či budově
export interface PlombaDiffItem {
  type: 'plomba';
  id?: number;
  cisloRizeni?: string;   // oficiální číslo řízení, např. "V-37641/2017-101"
  typNazev?: string;      // Vklad / Záznam / ...
  datumPrijeti?: string;
  stav?: string;
  operace?: PlombaOperace[];
  ukonceno?: boolean;     // true = řízení skončilo (plomba odstraněna)
}

// Typ pro vstupní data (diff formát)
interface RawBuildingLogData {
  diff?: Array<{
    type: string;
    label?: string;
    current?: string;
    previous?: string;
    added?: unknown[];
    removed?: unknown[];
  }>;
}

// Typ pro vstupní data (plomba formát z CuzkBuildingProcessoru)
interface RawPlombaData {
  plomby?: unknown[];
  ukonceno?: boolean;
}

// Typ pro novou strukturu akcí
interface BuildingLogAction {
  type: 'addUnit' | 'removeUnit' | 'addProfile' | 'removeProfile';
  unit?: string;
  unitId?: string;
  profileId?: string;
  profileName?: string;
}

// Union typ pro vstupní data
type BuildingLogInput = RawBuildingLogData | RawPlombaData | BuildingLogAction[];

export type DiffItem = ValueDiffItem | ListCollectionDiffItem | BuildingLogActionItem | PlombaDiffItem | InsolvencyDiffItem;

// Minimální tvar profilu pro párování se změnou vlastníka
interface OwnerProfile {
  id: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  [key: string]: unknown;
}

// Akademické tituly z katastru (data přicházejí lowercase přes Text::normalize na BE).
// Klíč = lowercase token tak, jak dorazí; canonical = správný tvar; position = před/za jménem.
const ACADEMIC_TITLES: Record<string, { canonical: string; position: 'before' | 'after' }> = {
  'prof.': { canonical: 'prof.', position: 'before' },
  'doc.': { canonical: 'doc.', position: 'before' },
  'dr.': { canonical: 'Dr.', position: 'before' },
  'bc.': { canonical: 'Bc.', position: 'before' },
  'bca.': { canonical: 'BcA.', position: 'before' },
  'mgr.': { canonical: 'Mgr.', position: 'before' },
  'mga.': { canonical: 'MgA.', position: 'before' },
  'ing.': { canonical: 'Ing.', position: 'before' },
  'mudr.': { canonical: 'MUDr.', position: 'before' },
  'mvdr.': { canonical: 'MVDr.', position: 'before' },
  'mddr.': { canonical: 'MDDr.', position: 'before' },
  'judr.': { canonical: 'JUDr.', position: 'before' },
  'phdr.': { canonical: 'PhDr.', position: 'before' },
  'rndr.': { canonical: 'RNDr.', position: 'before' },
  'pharmdr.': { canonical: 'PharmDr.', position: 'before' },
  'paeddr.': { canonical: 'PaedDr.', position: 'before' },
  'thlic.': { canonical: 'ThLic.', position: 'before' },
  'thdr.': { canonical: 'ThDr.', position: 'before' },
  'csc.': { canonical: 'CSc.', position: 'after' },
  'drsc.': { canonical: 'DrSc.', position: 'after' },
  'dis.': { canonical: 'DiS.', position: 'after' },
  'dis': { canonical: 'DiS.', position: 'after' },
  'ph.d.': { canonical: 'Ph.D.', position: 'after' },
  'phd.': { canonical: 'Ph.D.', position: 'after' },
  'th.d.': { canonical: 'Th.D.', position: 'after' },
  'msc.': { canonical: 'MSc.', position: 'after' },
  'll.m.': { canonical: 'LL.M.', position: 'after' },
  'mba': { canonical: 'MBA', position: 'after' },
};

// Marker společného jmění manželů na začátku řetězce (SJM / SJ / BSM)
const PAIR_MARKER = /^(sjm|sj|bsm)\b[\s,]*/i;

// Spojky/předložky, které ve jméně zůstávají malým písmenem
const LOWERCASE_NAME_WORDS = new Set(['a', 'i', 'o', 'u', 'v', 've', 'z', 'ze', 's', 'se']);

// Náznaky, že vlastník je právnická osoba (právní formy, veřejné subjekty).
// Porovnává se proti normalizovanému (bez diakritiky) řetězci.
const COMPANY_HINT = /\b(s\.?\s?r\.?\s?o|a\.?\s?s|v\.?\s?o\.?\s?s|k\.?\s?s|spol|spolecnost|spolecenstvi|svj|druzstvo|z\.?\s?s|o\.?\s?p\.?\s?s|akciova|komanditni|verejna obchodni|nadace|nadacni|fond|ustav|obec|mesto|kraj|ministerstvo|urad|ceska republika|statni)\b/;

// Normalizace pro porovnání jmen (bez diakritiky, lowercase)
const normalizeForMatch = (value: string): string =>
  value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

// Rozliší fyzickou osobu od firmy. Nejdřív podle profilů jednotky (pocházejí ze
// strukturovaných dat KN – OFO/OPO/BSM), pak heuristikou na právní formy.
const classifyOwner = (value: string, profiles?: OwnerProfile[]): 'person' | 'company' => {
  const normalized = normalizeForMatch(value);

  if (profiles && profiles.length > 0) {
    for (const profile of profiles) {
      if (profile.company && normalized.includes(normalizeForMatch(profile.company))) {
        return 'company';
      }
    }
    for (const profile of profiles) {
      const firstName = normalizeForMatch(profile.firstName || '');
      const lastName = normalizeForMatch(profile.lastName || '');
      if (!profile.company && firstName && lastName
        && normalized.includes(firstName) && normalized.includes(lastName)) {
        return 'person';
      }
    }
  }

  return COMPANY_HINT.test(normalized) ? 'company' : 'person';
};

// Kapitalizace slova vč. částí za pomlčkou/apostrofem (Nováková-Malá, O'Brien)
const capitalizeNameWord = (word: string): string => {
  if (LOWERCASE_NAME_WORDS.has(word.toLowerCase())) {
    return word.toLowerCase();
  }
  return word.replace(/(^|[-'’])(\p{L})/gu, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
};

// Kanonické tvary běžných právních forem (klíč = lowercase token)
const LEGAL_FORMS: Record<string, string> = {
  's.r.o.': 's.r.o.',
  'a.s.': 'a.s.',
  'spol.': 'spol.',
  'v.o.s.': 'v.o.s.',
  'k.s.': 'k.s.',
  'o.p.s.': 'o.p.s.',
  'z.s.': 'z.s.',
  'svj': 'SVJ',
};

// Naformátování názvu firmy – jen kapitalizace + kanonické právní formy, beze změny pořadí.
const formatCompany = (raw: string): string =>
  raw.trim().split(/\s+/).filter(Boolean)
    .map(token => LEGAL_FORMS[token.toLowerCase()] ?? capitalizeNameWord(token))
    .join(' ');

// Naformátování jedné fyzické osoby: tituly před jméno, DiS./CSc. apod. za jméno,
// pořadí "Jméno Příjmení" (katastr uvádí "Příjmení Jméno").
const formatPerson = (raw: string): string => {
  const tokens = raw.trim().split(/\s+/).filter(Boolean);
  const before: string[] = [];
  const after: string[] = [];
  const names: string[] = [];

  tokens.forEach(token => {
    const title = ACADEMIC_TITLES[token.toLowerCase()];
    if (title) {
      (title.position === 'before' ? before : after).push(title.canonical);
    } else {
      names.push(capitalizeNameWord(token));
    }
  });

  // Katastr uvádí "Příjmení Jméno" → přehodit na "Jméno Příjmení".
  // U více než dvou slov (složená jména/příjmení) pořadí nesaháme.
  if (names.length === 2) {
    names.reverse();
  }

  return [...before, ...names, ...after].join(' ');
};

// Naformátování hodnoty vlastníka z katastru pro zobrazení.
// Profily jednotky (volitelné) pomáhají odlišit osobu od firmy.
export const formatOwnerName = (raw: string, profiles?: OwnerProfile[]): string => {
  const value = (raw || '').trim();
  if (value === '') return '';

  const pairMatch = value.match(PAIR_MARKER);
  if (pairMatch) {
    const marker = pairMatch[1].toUpperCase(); // zachová tvar z katastru: SJ / SJM / BSM
    const rest = value.slice(pairMatch[0].length);
    const persons = rest.split(/\s+a\s+/i).map(formatPerson);
    return `${marker} ${persons.join(' a ')}`;
  }

  return classifyOwner(value, profiles) === 'company'
    ? formatCompany(value)
    : formatPerson(value);
};

// Vyfiltruje z profilů jednotky jen ty, kterých se reálně týká změna vlastníka.
// U garáží s desítkami spoluvlastníků tak nevypisujeme všechny, jen dotčené.
// Pokud změna není typu "Vlastník" (jiný rejstřík) nebo nic nesedne, vrátíme vše.
export const filterProfilesByOwnerChange = <T extends OwnerProfile>(
  profiles: T[] | undefined,
  diffItems: DiffItem[],
): T[] => {
  if (!profiles || profiles.length === 0) {
    return profiles ?? [];
  }

  const ownerValues = diffItems
    .filter((item): item is ValueDiffItem => item.type === 'value' && item.label === 'Vlastník')
    .flatMap(item => [item.previous, item.current]);

  if (ownerValues.length === 0) {
    return profiles; // jiný typ změny – necháme všechny profily
  }

  const haystack = normalizeForMatch(ownerValues.join(' '));

  const matched = profiles.filter(profile => {
    if (profile.company) {
      return haystack.includes(normalizeForMatch(profile.company));
    }
    const firstName = normalizeForMatch(profile.firstName || '');
    const lastName = normalizeForMatch(profile.lastName || '');
    if (!firstName || !lastName) {
      return false;
    }
    return haystack.includes(firstName) && haystack.includes(lastName);
  });

  return matched.length > 0 ? matched : profiles; // fallback: raději vše než nic
};

// Pole jednoho insolvenčního řízení z ISIR (klíče = slug z crawleru)
interface InsolvencyProceedingFields {
  id?: string;
  stav?: string;
  lhuta?: string;
  skonceni?: string;
}

const INSOLVENCY_SLUG_MAP: Record<string, keyof InsolvencyProceedingFields> = {
  'id-insolvencniho-rizeni': 'id',
  'aktualni-stav': 'stav',
  'konec-lhuty-pro-prihlaseni-pohledavek': 'lhuta',
  'datum-skonceni-insolvencniho-rizeni': 'skonceni',
};

// Jedno řízení = pole objektů {name, slug, value}. Vrací null, když tvar neodpovídá
// (pak se položka vykreslí obecným list/collection renderem jako dřív).
const parseInsolvencyProceeding = (raw: unknown): InsolvencyProceedingFields | null => {
  if (!Array.isArray(raw) || raw.length === 0) return null;

  const fields: InsolvencyProceedingFields = {};
  let recognized = false;

  for (const entry of raw) {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
    const { slug, value } = entry as Record<string, unknown>;
    if (typeof slug !== 'string') return null;

    const key = INSOLVENCY_SLUG_MAP[slug];
    if (key) {
      recognized = true;
      const text = typeof value === 'string' ? value.trim() : '';
      if (text !== '' && text !== '-') {
        fields[key] = text;
      }
    }
  }

  return recognized ? fields : null;
};

// Převod collection diffu insolvence na strukturované položky. Stejné řízení v added
// i removed (změna stavu) se spáruje podle id na jednu položku 'updated'.
const parseInsolvencyDiff = (added: unknown[], removed: unknown[]): InsolvencyDiffItem[] | null => {
  if (added.length + removed.length === 0) return null;

  const addedParsed = added.map(parseInsolvencyProceeding);
  const removedParsed = removed.map(parseInsolvencyProceeding);
  if ([...addedParsed, ...removedParsed].some(p => p === null)) {
    return null;
  }

  const items: InsolvencyDiffItem[] = [];
  const removedLeft = [...(removedParsed as InsolvencyProceedingFields[])];

  for (const current of addedParsed as InsolvencyProceedingFields[]) {
    const previousIndex = current.id
      ? removedLeft.findIndex(p => p.id === current.id)
      : -1;

    if (previousIndex >= 0) {
      const previous = removedLeft.splice(previousIndex, 1)[0];
      items.push({
        type: 'insolvency',
        change: 'updated',
        id: current.id,
        stav: current.stav,
        stavPrevious: previous.stav !== current.stav ? previous.stav : undefined,
        lhuta: current.lhuta,
        skonceni: current.skonceni,
      });
    } else {
      items.push({ type: 'insolvency', change: 'new', ...current });
    }
  }

  for (const previous of removedLeft) {
    items.push({ type: 'insolvency', change: 'removed', ...previous });
  }

  return items;
};

// Parser pro diff formát (struktura s diff objekty)
const parseDiffFormat = (data: RawBuildingLogData, profiles?: OwnerProfile[]): DiffItem[] => {
  if (!data?.diff || !Array.isArray(data.diff)) {
    return [];
  }

  return data.diff.flatMap((item): DiffItem[] | DiffItem => {
    if (item.type === 'value') {
      const label = item.label || 'Neznámá změna';
      const current = item.current || '';
      const previous = item.previous || '';
      return {
        type: 'value',
        label,
        current: label === 'Vlastník' ? formatOwnerName(current, profiles) : current,
        previous: label === 'Vlastník' ? formatOwnerName(previous, profiles) : previous,
      };
    }

    // Insolvenční řízení z ISIR mají vlastní strukturovaný render
    const insolvencyItems = parseInsolvencyDiff(item.added || [], item.removed || []);
    if (insolvencyItems !== null) {
      return insolvencyItems;
    }

    // Pro list a collection - strukturálně identické
    return {
      type: item.type as 'list' | 'collection',
      label: item.label,
      added: (item.added || []) as string[] | Record<string, unknown>[],
      removed: (item.removed || []) as string[] | Record<string, unknown>[]
    };
  });
};

// Parser pro plomba formát (probíhající/ukončená řízení z KN)
const parsePlombaFormat = (data: RawPlombaData): PlombaDiffItem[] => {
  return (data.plomby || []).map((raw): PlombaDiffItem => {
    // Legacy záznamy mají jen interní id řízení (number), nové plné objekty
    if (typeof raw === 'number') {
      return { type: 'plomba', id: raw, ukonceno: data.ukonceno };
    }
    const p = (raw || {}) as Record<string, unknown>;
    return {
      type: 'plomba',
      id: typeof p.id === 'number' ? p.id : undefined,
      cisloRizeni: typeof p.cisloRizeni === 'string' ? p.cisloRizeni : undefined,
      typNazev: typeof p.typNazev === 'string' ? p.typNazev : undefined,
      datumPrijeti: typeof p.datumPrijeti === 'string' ? p.datumPrijeti : undefined,
      stav: typeof p.stav === 'string' ? p.stav : undefined,
      operace: Array.isArray(p.operace)
        ? (p.operace as Record<string, unknown>[]).map(op => ({
            nazev: typeof op?.nazev === 'string' ? op.nazev : undefined,
            datum: typeof op?.datum === 'string' ? op.datum : undefined,
          }))
        : undefined,
      ukonceno: data.ukonceno,
    };
  });
};

// Parser pro nový formát akcí
const parseActionFormat = (actions: BuildingLogAction[]): BuildingLogActionItem[] => {
  return actions.map(action => ({
    type: 'action' as const,
    actionType: action.type,
    unit: action.unit,
    unitId: action.unitId,
    profileId: action.profileId,
    profileName: action.profileName
  }));
};

// Hlavní parsovací funkce - detekuje formát a použije odpovídající parser.
// Profily jednotky (volitelné) pomáhají odlišit fyzickou osobu od firmy při formátování jmen.
export const parseBuildingLogData = (data: BuildingLogInput, profiles?: OwnerProfile[]): DiffItem[] => {
  // Detekce formátu - pokud je to pole, jde o novou strukturu akcí
  if (Array.isArray(data)) {
    return parseActionFormat(data);
  }

  // Jinak jde o strukturu s diff
  if (data && typeof data === 'object' && 'diff' in data) {
    return parseDiffFormat(data, profiles);
  }

  // Plomba formát (řízení v katastru)
  if (data && typeof data === 'object' && 'plomby' in data && Array.isArray(data.plomby)) {
    return parsePlombaFormat(data);
  }

  return [];
};

// Zjištění typu položek v polích - buď stringy nebo objekty
export const getArrayItemType = (items: unknown[]): 'string' | 'object' => {
  if (items.length === 0) return 'string';
  
  const firstItem = items[0];
  return typeof firstItem === 'string' ? 'string' : 'object';
};

// Formátování hodnot pro zobrazení
export const formatValueForDisplay = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  return String(value);
};

// Univerzální formátování objektů
export const formatObject = (obj: Record<string, unknown>, _context?: string): string => {
  // Specifické formátování pro známé typy objektů
  
  // Pro owner objekty (vlastníci)
  if (obj.owner && obj.share) {
    return `${obj.owner} (podíl: ${obj.share})`;
  }
  
  // Fallback pro obecné statutory member objekty (pokud máme name a value)
  if (obj.name && obj.value) {
    return `${obj.name} ${obj.value}`;
  }
  
  // Fallback pro type a value
  if (obj.type && obj.value) {
    return `${obj.type}: ${obj.value}`;
  }
  
  // Zkusíme najít "hodnotu" objektu pomocí různých klíčů
  const valueKeys = ['value', 'name', 'title', 'label', 'id', 'owner'];
  
  for (const key of valueKeys) {
    if (obj[key] && typeof obj[key] === 'string') {
      return obj[key] as string;
    }
  }
  
  // Pokud nenajdeme známý klíč, zobrazíme všechny key-value páry
  const pairs = Object.entries(obj)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(', ');
  
  return pairs || 'Neznámý objekt';
};

// Formátování akcí pro zobrazení
export const formatActionForDisplay = (action: BuildingLogActionItem): string => {
  switch (action.actionType) {
    case 'addUnit':
      return `Přidána nová jednotka ${action.unit}`;
    case 'removeUnit':
      return `Odebrána jednotka ${action.unit}`;
    case 'addProfile':
      return `Přidán nový vlastník ${action.profileName} k jednotce ${action.unit}`;
    case 'removeProfile':
      return `Odebrán vlastník ${action.profileName} z jednotky ${action.unit}`;
    default:
      return `Akce ${action.actionType}`;
  }
};

// Formátování odkazů na jednotky pro HTML summary
const formatUnitLinksHtml = (units: Array<{id: string; label: string}>): string => {
  const maxUnits = 3;
  const displayed = units.slice(0, maxUnits);
  const links = displayed.map(
    u => `<a href="/units/unit/${u.id}" class="BuildingLogModal__unitLink">${u.label}</a>`
  );
  const rest = units.length - maxUnits;
  if (rest > 0) {
    links.push(`(+${rest})`);
  }
  return links.join(', ');
};

// Generování shrnutí změn pro building log
export const generateChangeSummary = (
  diffItems: DiffItem[],
  units?: Array<{id: string; label: string}>
): string => {
  if (diffItems.length === 0) {
    return "Bez detailních informací o změnách";
  }

  const changedLabels: string[] = [];

  diffItems.forEach(item => {
    if (item.type === 'value') {
      // Pro label "Vlastník" zobrazíme i hodnoty
      if (item.label === 'Vlastník') {
        changedLabels.push(`${item.label} (<strong>${item.previous} → ${item.current}</strong>)`);
      } else {
        changedLabels.push(item.label);
      }
    } else if (item.type === 'list' || item.type === 'collection') {
      if (item.label) {
        changedLabels.push(item.label);
      }
    } else if (item.type === 'action') {
      // Pro akce použijeme formátované zobrazení
      changedLabels.push(formatActionForDisplay(item));
    } else if (item.type === 'insolvency') {
      if (item.change === 'new') {
        changedLabels.push(`Nové insolvenční řízení${item.stav ? ` (<strong>${item.stav}</strong>)` : ''}`);
      } else if (item.change === 'updated') {
        changedLabels.push(
          item.stavPrevious && item.stav
            ? `Insolvenční řízení: <strong>${item.stavPrevious} → ${item.stav}</strong>`
            : 'Změna insolvenčního řízení'
        );
      } else {
        changedLabels.push('Insolvenční řízení již není v rejstříku vedeno');
      }
    } else if (item.type === 'plomba') {
      const rizeniLabel = item.cisloRizeni
        ? `${item.cisloRizeni}${item.typNazev ? ` (${item.typNazev})` : ''}`
        : '';
      changedLabels.push(
        `${item.ukonceno ? 'Ukončené řízení' : 'Probíhající řízení'}${rizeniLabel ? ` <strong>${rizeniLabel}</strong>` : ''}`
      );
    }
  });

  if (changedLabels.length === 0) {
    return "Změna";
  }

  // Délku neořezáváme v JS (riskovalo by useknutí HTML uprostřed tagu) — zalomení
  // na max 2 řádky řeší CSS (.BuildingLogItem__msg, line-clamp).
  const labelsText = changedLabels.join(', ');

  // Prefix s odkazy na jednotky
  const unitPrefix = units && units.length > 0
    ? `Změna u jednotek ${formatUnitLinksHtml(units)}: `
    : 'Změna: ';

  return `${unitPrefix}${labelsText}`;
};
